import { useMemo } from "react";
import { Box, HStack, Icon, LinkBox, LinkOverlay, MenuItem, Skeleton, VStack, useDisclosure } from "@chakra-ui/react";
import CDS from "design-system";
import { Link as RouterLink } from "react-router";
import { LuArchive, LuChevronRight, LuTriangleAlert, LuListChecks, LuCircleCheck, LuClock, LuUserCheck, LuCompass } from "react-icons/lu";
import { FiLogOut, FiUserX } from "react-icons/fi";
import { NewHireJourney } from "models/v2/newHireJourney";
import { User } from "features/User/models";
import { newHireJourneyPath } from "helpers/url/pages/v1";
import { dayAndPeriodLabel } from "helpers/time";
import ProfileAvatar from "components/ProfileAvatar";
import { VThreeDotMenu } from "components/Menu/ThreeDotMenu";
import MenuItemIcon from "components/Icon/MenuItemIcon";
import { JourneyAttention, JourneyAttentionStatus } from "features/NewHireJourney/hooks";
import { canCloseJourney, viewerRolesOnJourney, viewerRoleLabel } from "features/NewHireJourney/helpers";
import CloseJourneyModal from "features/NewHireJourney/Components/CloseJourneyModal";

interface NewHireJourneyListRowProps {
  journey: NewHireJourney;
  currentUser: User | undefined;
  attention?: JourneyAttention;
  onSelect?: (journey: NewHireJourney) => void;
}

const STATUS_PILL: Record<
  JourneyAttentionStatus,
  { label: string; bg: string; color: string; icon: React.ElementType }
> = {
  needs_attention: { label: "Needs Attention", bg: "red.100", color: "red.800", icon: LuTriangleAlert },
  tasks_overdue: { label: "Overdue Tasks", bg: "orange.50", color: "orange.700", icon: LuClock },
  tasks_open: { label: "Open Tasks", bg: "gray.100", color: "gray.700", icon: LuListChecks },
  all_clear: { label: "All Clear!", bg: "green.50", color: "green.700", icon: LuCircleCheck },
};

export default function NewHireJourneyListRow({ journey, currentUser, attention, onSelect }: NewHireJourneyListRowProps) {
  const closeModal = useDisclosure();
  const closeAndDeactivateModal = useDisclosure();
  const canCloseFromRow = canCloseJourney(journey, currentUser);
  const newHireIsActive = !!journey.newHire && !journey.newHire.deactivatedAt;

  const isLoading = !attention;
  const openCount = attention?.openCount ?? 0;
  const status = attention?.attentionStatus ?? "all_clear";
  const pill = STATUS_PILL[status];

  const dayLabel = dayAndPeriodLabel(journey.startDate);
  const viewerRoles = useMemo(() => viewerRolesOnJourney(currentUser, journey), [currentUser, journey]);

  const interactionProps = onSelect
    ? {
      role: "button" as const,
      tabIndex: 0,
      onClick: () => onSelect(journey),
      onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(journey);
        }
      },
      cursor: "pointer" as const,
      width: "100%",
    }
    : { as: RouterLink, to: newHireJourneyPath(journey) };

  return (
    <>
    <HStack
      {...interactionProps}
      spacing={3}
      px={4}
      py={3}
      borderBottomWidth="1px"
      borderColor="gray.100"
      _last={{ borderBottomWidth: 0 }}
      _hover={{ bg: "gray.50", textDecoration: "none" }}
      align="center"
    >
      <ProfileAvatar user={journey.newHire} size="sm" />

      <VStack align="start" spacing={0.5} minW={0} flex="1">
        <HStack spacing={2} minW={0} flexWrap="wrap">
          <HStack spacing={1} minW={0}>
            <LinkBox onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              <CDS.Text12 fontWeight="semibold" color="gray.900" noOfLines={1}>
                <LinkOverlay as={RouterLink} to={newHireJourneyPath(journey)} _hover={{ textDecoration: "underline" }}>
                  {journey.newHire?.fullName}
                </LinkOverlay>
              </CDS.Text12>
            </LinkBox>
            {journey.newHire?.title && (
              <CDS.Text12 color="gray.600" noOfLines={1}>
                · {journey.newHire.title}
              </CDS.Text12>
            )}
          </HStack>
          <HStack spacing={1} color="gray.500" fontSize="xs" flexShrink={0}>
            <Icon as={LuCompass} boxSize={3} />
            <CDS.Text12 as="span">Onboarding</CDS.Text12>
          </HStack>
        </HStack>
        <HStack spacing={2} flexWrap="wrap" rowGap={1}>
          {dayLabel && (
            <CDS.Text10 color="gray.500" whiteSpace="nowrap">
              {dayLabel}
            </CDS.Text10>
          )}
          {viewerRoles.length > 0 && (
            <HStack
              as="span"
              spacing={1.5}
              px={2.5}
              py={0.5}
              borderRadius="full"
              borderWidth="1px"
              borderColor="blue.200"
              color="blue.600"
              fontSize="xs"
              fontWeight="medium"
              whiteSpace="nowrap"
            >
              <Icon as={LuUserCheck} boxSize={3.5} />
              <CDS.Text10 as="span" fontWeight="medium">
                {`You: ${viewerRoles.map(viewerRoleLabel).join(" + ")}`}
              </CDS.Text10>
            </HStack>
          )}
        </HStack>
      </VStack>

      <HStack spacing={3} flexShrink={0}>
        {journey.status !== "closed" && !isLoading && openCount > 0 && (
          <CDS.Text10 color="gray.500" whiteSpace="nowrap">
            {openCount} Open Task{openCount === 1 ? "" : "s"}
          </CDS.Text10>
        )}

        {journey.status === "closed" ? (
          <HStack
            as="span"
            spacing={1.5}
            px={2.5}
            py={1}
            borderRadius="md"
            bg="gray.100"
            color="gray.700"
            fontSize="xs"
            fontWeight="medium"
            whiteSpace="nowrap"
          >
            <Icon as={LuArchive} boxSize={3.5} />
            <CDS.Text10 as="span" fontWeight="medium">Closed</CDS.Text10>
          </HStack>
        ) : isLoading ? (
          <Skeleton height="22px" width="120px" borderRadius="md" />
        ) : (
          <HStack
            as="span"
            spacing={1.5}
            px={2.5}
            py={1}
            borderRadius="md"
            bg={pill.bg}
            color={pill.color}
            fontSize="xs"
            fontWeight="medium"
            whiteSpace="nowrap"
          >
            <Icon as={pill.icon} boxSize={3.5} />
            <CDS.Text10 as="span" fontWeight="medium">{pill.label}</CDS.Text10>
          </HStack>
        )}

        {canCloseFromRow && (
          <Box
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <VThreeDotMenu size="xs" inPortal menuFontSize="sm" ariaLabel="Journey options" color="gray.500" _hover={{ color: "gray.800" }}>
              <MenuItem
                icon={<MenuItemIcon icon={FiLogOut} />}
                fontSize="sm"
                py={1.5}
                onClick={closeModal.onOpen}
              >
                End Journey Early
              </MenuItem>
              {newHireIsActive && (
                <MenuItem
                  icon={<MenuItemIcon icon={FiUserX} />}
                  fontSize="sm"
                  py={1.5}
                  color="red.600"
                  onClick={closeAndDeactivateModal.onOpen}
                >
                  End Journey & Deactivate User
                </MenuItem>
              )}
            </VThreeDotMenu>
          </Box>
        )}

        <Icon as={LuChevronRight} boxSize={4} color="gray.400" />
      </HStack>
    </HStack>
    {canCloseFromRow && (
      <>
        <CloseJourneyModal
          isOpen={closeModal.isOpen}
          onClose={closeModal.onClose}
          journeyId={journey.id}
          newHireName={journey.newHire?.fullName ?? "this new hire"}
        />
        {newHireIsActive && journey.newHire && (
          <CloseJourneyModal
            isOpen={closeAndDeactivateModal.isOpen}
            onClose={closeAndDeactivateModal.onClose}
            journeyId={journey.id}
            newHireName={journey.newHire.fullName}
            withUserDeactivation
            newHire={journey.newHire}
          />
        )}
      </>
    )}
    </>
  );
}
